/**
 * A CHANGE REQUEST, WRITTEN FROM THE BOARD RATHER THAN FILLED IN — #195.
 *
 * WHAT THIS IS FOR. CPFC's change request form for Loan Hub 1.4.0 is the shape
 * TRINRE already work in, and almost all of it is already on the board. One of
 * its item lines reads
 *
 *     Backlog LH025 · New Feature · Priority: High · Difficulty: 4 · Requested 07/07/2026
 *
 * and every part of that is a field on a card. The sections above the items are
 * epics. "Items Deferred" is the blocked cards, whose reasons the board already
 * knows. So the document is not a form somebody fills in: it is generated, and
 * the only parts a person writes are the three paragraphs nobody could infer —
 * Change Description, Change Reason, Impact of Change.
 *
 * WHY IT IS PURE FUNCTIONS AND NOT A SCREEN. Everything here is "given these
 * cards, what does the document say", which is the part that has to be right and
 * the part a browser makes slow to check. The screen renders what these return.
 *
 * WHAT IS NOT HERE, ON PURPOSE. Nothing formats a PDF. A PDF freezes what the
 * document said at the moment it was signed, and that is a separate job from
 * working out what it says — which is regenerated from the board every time the
 * draft is opened, so correcting a card and generating again is the way to fix
 * a wrong item line. Vinnie, 22 August 2026: item lines come from the board, so
 * the screen has to say why they cannot be typed over.
 */
import { blockerOf } from './blocked';
import type { Issue, IssuePriority, IssueStatus } from './types';
import { PRIORITY_LABELS, STATUS_LABELS } from './types';
import { ORIGINAL_TYPES, labelOf, type IssueType } from './issue-types';

/**
 * THE FIELDS THAT MAY APPEAR ON AN ITEM LINE.
 *
 * A CLOSED LIST RATHER THAN ANY FIELD OF A CARD, because a change request is
 * shown to a customer and signed. Opening it to everything would put a card's
 * internal notes, its blocker or whoever it is assigned to into a document that
 * leaves the building, one careless template at a time.
 */
export type ItemField = 'status' | 'ref' | 'kind' | 'priority' | 'difficulty' | 'created';

/**
 * WHAT A CUSTOMER'S CHANGE REQUEST LOOKS LIKE, WITHOUT A FORM BUILDER.
 *
 * Vinnie's decision of 22 August 2026: customisable by TEMPLATE. A template
 * says which fields go on an item line and what each is CALLED — CPFC say
 * "Difficulty" where another customer says "Effort" — the narrative sections and
 * their order, and the words allowed in the assumptions table. It does not let
 * anybody invent a field, which is the difference between a template and a form
 * builder and the reason this stays safe to sign.
 */
export interface ChangeRequestTemplate {
  name: string;
  /**
   * In the order they are printed.
   *
   * `label` IS PRINTED VERBATIM before the value, separated by a space — so it
   * carries its own colon or does not. CPFC write "Priority: High" and
   * "Requested 07/07/2026" on the same line, and a template that added the
   * colon itself could produce only one of those.
   *
   * `runOn` joins a part to the one before it with a space instead of the
   * separator, which is how their line opens "Backlog LH025" rather than
   * "Backlog · LH025". Left off, every part is its own.
   */
  itemLine: { field: ItemField; label: string; runOn?: boolean }[];
  narratives: { key: string; heading: string; help: string }[];
  /** The words a business decision may take. First is the default. */
  decisions: string[];
  deferredHeading: string;
}

/**
 * THE ONE THAT SHIPS, matching the form Vinnie sent on 22 August 2026.
 *
 * The item line is field-for-field CPFC's, including the two labels they write
 * out in full and the three they do not.
 */
export const STANDARD_FORM: ChangeRequestTemplate = {
  name: 'Standard change request',
  itemLine: [
    { field: 'status', label: '' },
    { field: 'ref', label: '', runOn: true },
    { field: 'kind', label: '' },
    { field: 'priority', label: 'Priority:' },
    { field: 'difficulty', label: 'Difficulty:' },
    { field: 'created', label: 'Requested' },
  ],
  narratives: [
    {
      key: 'description',
      heading: 'Change Description',
      help: 'What is changing, in the words the client would use.',
    },
    {
      key: 'reason',
      heading: 'Change Reason',
      help: 'Why it is being asked for, and what happens if it is not done.',
    },
    {
      key: 'impact',
      heading: 'Impact of Change',
      help: 'What this costs — time, other work delayed, anything it puts at risk.',
    },
  ],
  decisions: ['To be confirmed', 'Confirmed', 'Deferred'],
  deferredHeading: 'Items Deferred',
};

export interface Assumption {
  text: string;
  decision: string;
}

/** The parts a person owns. Everything else on the page comes from the board. */
export interface WrittenByHand {
  narratives: Record<string, string>;
  assumptions: Assumption[];
}

export interface ItemLine {
  id: string;
  ref: string;
  title: string;
  /**
   * WHAT THE ITEM IS, IN PLAIN ENGLISH — #228. The card's own plain-English
   * summary, the field written "for the reader who signs" rather than the
   * technical notes. Printed under the title so a signatory can tell the work
   * apart from its administrative line. The fuller "description / technical
   * notes" field is deliberately never carried here (Vinnie, 5 Oct 2026) — a
   * team's internal notes must not leave the building on a signed document.
   *
   * THREE STATES, ON PURPOSE. `undefined` is a change request frozen before this
   * field existed (Doc.scope, #195/0088): its item lines carry no summary, and
   * leaving it undefined lets the PDF keep those documents exactly as they were
   * signed. A freshly generated line always carries a string — empty when the
   * card has no summary — so the EDITOR can prompt for the missing one while the
   * produced PDF simply omits it (a customer never sees "add one on the card").
   */
  summary?: string;
  /** Already joined with the separator, ready to print. */
  detail: string;
  /** Only on deferred items — the board's own reason it cannot move. */
  why?: string;
}

export interface ChangeRequestSection {
  heading: string;
  items: ItemLine[];
}

export interface ChangeRequest {
  sections: ChangeRequestSection[];
  deferred: ItemLine[];
  /** Every item counted once, deferred included. What the summary line says. */
  total: number;
}

/** CPFC write 07/07/2026. Day first, which is what the Caribbean and the UK read. */
export const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * THE CLOCK TIME OF A SIGNATURE, to the minute, 24-hour — Vinnie, 11 September
 * 2026: an audit trail records when as well as who, and a date alone cannot tell
 * two signatures apart on the day they matter most. Local to wherever the record
 * is read, which is the same instant `asDate` shows and the same one stored.
 */
export const asTime = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getHours())}:${two(d.getMinutes())}`;
};

/**
 * One field of one card, as the document prints it.
 *
 * AN UNSET DIFFICULTY PRINTS NOTHING AT ALL, and the caller drops the part
 * rather than printing "Difficulty: —". Nobody has judged it, which is not the
 * same as judging it to be nothing, and a change request that invents a number
 * is a change request that lies about what was agreed. See migration 0060.
 */
const valueOf = (issue: Issue, field: ItemField, types: IssueType[]): string => {
  switch (field) {
    case 'status':
      return STATUS_LABELS[issue.status as IssueStatus] ?? '';
    case 'ref':
      return issue.ref ?? '';
    case 'kind':
      /* The board's own name for it — #302. A change request naming a type
         the customer does not use is a document they cannot check. */
      return labelOf(issue.kind, types);
    case 'priority':
      return PRIORITY_LABELS[issue.priority as IssuePriority] ?? '';
    case 'difficulty':
      return issue.difficulty === undefined || issue.difficulty === null
        ? ''
        : String(issue.difficulty);
    case 'created':
      return issue.createdAt ? asDate(issue.createdAt) : '';
  }
};

/**
 * `Backlog LH025 · New feature · Priority: High · Difficulty: 4 · Requested 07/07/2026`
 *
 * A PART WITH NO VALUE TAKES ITS LABEL WITH IT rather than printing a heading
 * over nothing, which is what keeps an unjudged difficulty off the line
 * entirely instead of printing "Difficulty:" and stopping.
 *
 * A `runOn` part whose predecessor is missing simply starts a part of its own,
 * so a card with no status still reads "LH025 · …" rather than opening with a
 * stray space.
 */
export const itemDetail = (
  issue: Issue,
  template: ChangeRequestTemplate,
  /* The board's own types — #302. Defaulted so every caller and check that
     does not care about naming keeps working unchanged. */
  types: IssueType[] = ORIGINAL_TYPES
): string => {
  const parts: string[] = [];
  for (const { field, label, runOn } of template.itemLine) {
    const value = valueOf(issue, field, types);
    if (!value) continue;
    const printed = label ? `${label} ${value}` : value;
    if (runOn && parts.length > 0) parts[parts.length - 1] += ` ${printed}`;
    else parts.push(printed);
  }
  return parts.join(' · ');
};

/**
 * The heading a card is filed under.
 *
 * A CARD WITH NO EPIC IS NOT DROPPED. It goes under a plain heading of its own,
 * because the alternative — quietly leaving it out — produces a document that
 * looks complete and is not, and nobody reading it could tell.
 */
const UNFILED = 'Other items';

/**
 * The whole document, from the cards of one sprint.
 *
 * DEFERRED IS DECIDED BY THE BOARD, not by the person writing. A card is
 * deferred when it is blocked or when it has been dropped, which are the two
 * ways work in a sprint does not get done. Both carry their reason already.
 *
 * ORDER IS BY EPIC NAME, then by the card's own reference — so generating the
 * same sprint twice produces the same document, which matters for something
 * that gets signed and compared against the last one.
 */
export function changeRequestFrom(
  issues: Issue[],
  template: ChangeRequestTemplate = STANDARD_FORM,
  types: IssueType[] = ORIGINAL_TYPES
): ChangeRequest {
  const line = (issue: Issue): ItemLine => ({
    id: issue.id,
    ref: issue.ref ?? '',
    title: issue.title ?? '',
    // The plain-English summary, not the technical notes (#228). Always a
    // string here — empty when the card has none — so the editor can prompt for
    // a missing summary; only frozen older snapshots leave it undefined, which
    // keeps a signed document exactly as it was.
    summary: (issue.summary ?? '').trim(),
    detail: itemDetail(issue, template, types),
    ...(blockerOf(issue) ? { why: blockerOf(issue) } : {}),
  });

  /* A card whose reason is the word "No" is not deferred, and must not go out
     to a customer under "held back" with "No" printed as the explanation. */
  const isDeferred = (i: Issue) => !!blockerOf(i) || i.status === 'wontdo';
  const byRef = (a: Issue, b: Issue) => (a.ref ?? '').localeCompare(b.ref ?? '', undefined, { numeric: true });

  const deferred = issues.filter(isDeferred).sort(byRef).map(line);

  const grouped = new Map<string, Issue[]>();
  for (const issue of issues.filter((i) => !isDeferred(i))) {
    const heading = issue.epic?.trim() || UNFILED;
    grouped.set(heading, [...(grouped.get(heading) ?? []), issue]);
  }

  const sections = [...grouped.entries()]
    .sort(([a], [b]) => {
      // The unfiled heading sits last however it sorts alphabetically: it is a
      // leftover, not a section of the document somebody planned.
      if (a === UNFILED) return 1;
      if (b === UNFILED) return -1;
      return a.localeCompare(b);
    })
    .map(([heading, items]) => ({ heading, items: items.sort(byRef).map(line) }));

  return { sections, deferred, total: sections.reduce((n, s) => n + s.items.length, 0) + deferred.length };
}

/*
 * HAS THE SCOPE DRIFTED FROM WHAT WAS SIGNED — #195, 0088.
 *
 * Compares a live change request against the snapshot a signed one carries, so
 * a sprint whose cards have moved since it was authorised can say so. Order does
 * not matter: the snapshot comes back from jsonb, which does not keep it, and a
 * card reordered is not a card changed. Each item is flattened to the fields a
 * reader would notice — which section it is in, its title and its detail — and
 * add, remove or edit shows up as a line that is in one set and not the other.
 *
 * THE PER-ITEM SUMMARY IS DELIBERATELY NOT PART OF SCOPE — #228. Scope is which
 * work is in, and its standing; the plain-English summary is prose describing
 * it, not the work itself. Folding it in here would also read every document
 * frozen before the summary existed as "drifted" the moment its cards had one,
 * which is a false alarm on history. The metadata line still catches a status
 * or priority change.
 */
export function sameScope(a: ChangeRequest, b: ChangeRequest): boolean {
  const flat = (r: ChangeRequest): string[] =>
    [
      ...r.sections.flatMap((s) => s.items.map((i) => `${s.heading} ${i.id} ${i.title} ${i.detail}`)),
      ...r.deferred.map((i) => ` d ${i.id} ${i.title} ${i.detail} ${i.why ?? ''}`),
    ].sort();
  const fa = flat(a);
  const fb = flat(b);
  return fa.length === fb.length && fa.every((x, i) => x === fb[i]);
}

/** A fresh set of the parts a person writes, for a document just generated. */
export const nothingWrittenYet = (template: ChangeRequestTemplate = STANDARD_FORM): WrittenByHand => ({
  narratives: Object.fromEntries(template.narratives.map((n) => [n.key, ''])),
  assumptions: [],
});

/**
 * HOW MUCH OF THE PART A PERSON OWNS IS DONE.
 *
 * Vinnie asked for this on 22 August 2026 — *"the bar counts how many of the
 * three are written, so somebody can stop and come back."* Assumptions are NOT
 * counted: a change request with no assumptions is a perfectly ordinary one, and
 * counting them would make an empty table look like unfinished work.
 */
export const howMuchIsWritten = (
  written: WrittenByHand,
  template: ChangeRequestTemplate = STANDARD_FORM
): { done: number; of: number } => ({
  done: template.narratives.filter((n) => (written.narratives[n.key] ?? '').trim().length > 0).length,
  of: template.narratives.length,
});

/**
 * WHAT STOPS THE DOCUMENT BEING PRODUCED, in the words somebody would fix it by.
 *
 * AN UNWRITTEN PARAGRAPH DOES NOT STOP IT, and that is Vinnie's correction of
 * 23 August 2026: *"All the fields have to be filled out for the form to be
 * produced. We should be able to produce the form."* The first version refused
 * until all three were written, on the reasoning that a change request sent
 * unread is the failure the document exists to prevent. That reasoning was
 * about SENDING, and it was applied to producing — which is a different act.
 * Somebody producing a draft to read on paper, or to show a colleague before
 * writing the narrative, was blocked by a rule aimed at somebody else.
 *
 * An unwritten section is simply not printed — see the PDF — so what comes out
 * is a shorter document rather than one with a heading over nothing.
 *
 * WHAT STILL STOPS IT is a document that would say nothing at all, and a table
 * with an empty row in it. The first is not a document; the second is a line
 * on a signed page that means nothing and cannot be read as anything.
 */
export function whyNotReady(
  request: ChangeRequest,
  written: WrittenByHand,
  _template: ChangeRequestTemplate = STANDARD_FORM
): string {
  if (request.total === 0) return 'This sprint has no items, so there is nothing to request.';
  const blank = written.assumptions.filter((a) => !a.text.trim()).length;
  if (blank > 0) return `${blank === 1 ? 'An assumption has' : `${blank} assumptions have`} been left empty.`;
  return '';
}
